import React, { useState, useEffect, useCallback } from "react";
import { useParams } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Text,
  Heading,
  Button,
  useToast,
  useColorModeValue,
  Flex,
  VStack,
  HStack,
  Badge,
  Select,
  Textarea,
  Switch,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Category } from '../../Platform/Common/types';

interface TokenPayload {
    sub: string;
    role_id: number;
    tenant_id: number;
    exp?: number;
  }

  const B2CReviewCategory = () => {
    // ────────────────────────────────────────────────────────────────────────────
    // Theme & routing hooks
    // ────────────────────────────────────────────────────────────────────────────

    const cardbg = useColorModeValue("gray.50", "gray.700");
  
    const { category_id } = useParams<{ category_id: string }>();
    const [categoryId, setCategoryId] = useState<number>(Number(category_id));
    const navigate = useNavigate();
    const toast = useToast();

  
    // ────────────────────────────────────────────────────────────────────────────
    // State: core category, question-sets, mappings
    // ────────────────────────────────────────────────────────────────────────────

  
    const [category, setCategory] = useState<Category>({
        category_id: 0, 
        name: '', 
        slug:'', 
        description: '', 
        long_description:'', 
        hero_text:'', 
        hero_headline:'', 
        sequence:0, 
        meta_description:'', 
        meta_title:'' , 
        is_active: true, 
        promo_headline: '',
        promo_text: '',
        promo_coupon_code: '',
        promo_active: false,
        created_at: new Date(), 
        updated_at: new Date()
    });

  
    const [isCategoryEdited, setIsCategoryEdited] = useState(false);

    const [editSessionKey, setEditSessionKey] = useState(0);
  
  
    // ────────────────────────────────────────────────────────────────────────────
    // Mode & button logic
    // ────────────────────────────────────────────────────────────────────────────
    const [isEditing, setIsEditing] = useState(false);
    const isCreating = categoryId === 0;
    const isViewMode = !isEditing;

  
    const showActionButtons    = !isCreating;
    const showSaveButton       = isCreating || !isViewMode;
    const isUpdateButtonEnabled   = showActionButtons && isViewMode ;
 

  
    // ────────────────────────────────────────────────────────────────────────────
    // API fetchers
    // ────────────────────────────────────────────────────────────────────────────
    const fetchCategory = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const { data } = await axios.get<Category>(`${b2c_apiurl}/category/${category_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setCategory(data);
    };



    // ────────────────────────────────────────────────────────────────────────────
    // Derived state effects
    // ────────────────────────────────────────────────────────────────────────────

    const loadAllData = useCallback(async () => {
        const token = localStorage.getItem("b2c_token");
        if (!token) return;
      
        // 2) if editing an existing category
        if (Number(category_id) > 0) {
          await fetchCategory();

          setCategoryId(Number(category_id));
        }
     
        // 4) reset any “edited” flags
        setIsEditing(false);
        setIsCategoryEdited(false);
      }, [
        category_id,
        fetchCategory,
 
      ]);

    // 1) On load —  fetch everything
    useEffect(() => {
      (async () => {
        const token = localStorage.getItem("b2c_token");
        if (!token) return;
  
  
        if (Number(category_id) > 0) {
          await fetchCategory();

          setCategoryId(Number(category_id));
        }

      })();
    }, [category_id]);

  

  
    // ────────────────────────────────────────────────────────────────────────────
    // Form handlers
    // ────────────────────────────────────────────────────────────────────────────
const handleInputChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
) => {
            const { name, value, type } = e.target;

            const finalValue =
              type === "checkbox"
                ? (e.target as HTMLInputElement).checked
                : value;

  setCategory(prev => ({
    ...prev,
    [name]: finalValue,
  }));

  setIsCategoryEdited(true);
};


  
    // ────────────────────────────────────────────────────────────────────────────
    // Save / Create / Update / Publish / Cancel / Terminate
    // ────────────────────────────────────────────────────────────────────────────
    const handleCreateCategory = async () => {
      try {
        const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
        console.log("Practice Set", category);
        const { data } = await axios.post<Category>(`${b2c_apiurl}/create-category/`, category, {
          headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
        });
        const newId = data.category_id;
        // re-map question-sets

        toast({ title: "Category created", status: "success" });

        setIsCategoryEdited(false);

        navigate(`/practice/app/b2c-category/${newId}/review-category`);
      
     } catch (err: any) {
    console.error(err);

        const message =
          err?.response?.data?.detail ||
          "Error creating category";

        toast({
          title: message,
          status: "error",
        });
      }
    };
  
    const handleUpdateCategory = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      // guard
      try {
        await axios.put(`${b2c_apiurl}/update-category/${category_id}`, category, {
          headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
        });
        toast({ title: "Category updated", status: "success" });
        setIsCategoryEdited(false);
        loadAllData();
     } catch (err: any) {
    console.error(err);

        const message =
          err?.response?.data?.detail ||
          "Error updating category";

        toast({
          title: message,
          status: "error",
        });
      }
    };

    
  
    const handleSaveCategory = () => {

      isCreating ? handleCreateCategory() : handleUpdateCategory();
    //  setIsCategoryEdited(false);
    };
  
    const handleUpdateClick = async () => {
        setIsEditing(true);
          // start a brand new edit session
        setEditSessionKey(prev => prev + 1);
    }


      const handleCancelOperation = () =>{
        setIsEditing(false);  
         loadAllData();
           // invalidate ALL child component state
        setEditSessionKey(prev => prev + 1);
        };

    

  return (
    <Box as="main" mx="auto" p={10} >

 {/* Category Details Top Bar */}
    <Box bg={useColorModeValue("gray.100", "gray.700")} p={4} borderRadius="sm" mb={4}>
    <Flex direction={["column", "row"]} justify="space-between" align={["flex-start", "center"]} wrap="wrap">
        <Heading size="lg">{category.name || "Untitled Category"}</Heading>
        <HStack spacing={2} mt={[2, 0]}>
        
        </HStack>
    </Flex>

    <Flex mt={3} wrap="wrap" fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
        <Text mr={4}><strong>Created:</strong> {new Date(category.created_at).toLocaleDateString()}</Text>
        <Text><strong>Updated:</strong> {new Date(category.updated_at).toLocaleDateString()}</Text>
    </Flex>
    </Box>

{/* Operations Buttons */}
    <Box>
    {showActionButtons && (
        <HStack spacing={2} mt={4}>
            <Button
            size="sm"
            borderRadius="sm"
            onClick={handleUpdateClick}
            isDisabled={!isUpdateButtonEnabled}
            colorScheme="blue"
            >
            Update Category
            </Button>


          {isEditing && (
            <Button size={"sm"} variant="outline" colorScheme="red" onClick={handleCancelOperation}>
              Cancel
            </Button>
          )}

        </HStack>
        )}
    </Box>

    {/* Category Title & Description */}
{/* ===========================
   CATEGORY REVIEW CONTAINER
=========================== */}
<Box mt={4}>

  {/* ===========================
      CORE INFORMATION
  =========================== */}
  <Box bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
    <fieldset disabled={!showSaveButton}>
      <Text mb={4} fontSize="md" fontWeight="bold">
        Core Information
      </Text>

      <VStack spacing={4} align="stretch">

        {/* Title */}
        <FormControl id="name" isRequired>
          <Input
            name="name"
            placeholder="Category Title*"
            size="sm"
            value={category.name}
            onChange={handleInputChange}
          />
        </FormControl>

        {/* Slug */}
        <FormControl id="slug" isRequired>
          <Input
            name="slug"
            placeholder="Slug*"
            size="sm"
            value={category.slug}
            onChange={handleInputChange}
          />
        </FormControl>

        {/* Description */}
        <FormControl id="description">
          <Textarea
            name="description"
            placeholder="Category Description"
            size="sm"
            value={category.description || ""}
            onChange={handleInputChange}
          />
        </FormControl>

          {/* long Description */}
        <FormControl id="long_description">
          <Textarea
            name="long_description"
            placeholder="Domain Long Description"
            size="sm"
            value={category.long_description || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl id="hero_headline">
          <Input
            name="hero_headline"
            placeholder="Hero Headline"
            size="sm"
            value={category.hero_headline || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl id="hero_text">
          <Textarea
            name="hero_text"
            placeholder="Hero Text"
            size="sm"
            value={category.hero_text || ""}
            onChange={handleInputChange}
          />
        </FormControl>


        {/* Active Toggle */}
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="is_active" mb="0" fontSize="sm">
            Active
          </FormLabel>
          <Switch
            id="is_active"
            name="is_active"
            isChecked={category.is_active}
            onChange={handleInputChange}
          />
        </FormControl>

      </VStack>
    </fieldset>
  </Box>

  {/* ===========================
    PROMOTION SECTION
=========================== */}
<Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
  <fieldset disabled={!showSaveButton}>
    <Text mb={4} fontSize="md" fontWeight="bold">Promotion</Text>

    <VStack spacing={4} align="stretch">
      <FormControl id="promo_headline">
        <Input name="promo_headline" placeholder="Promo Headline" size="sm" value={category.promo_headline || ""} onChange={handleInputChange} />
      </FormControl>

      <FormControl id="promo_text">
        <Textarea name="promo_text" placeholder="Promo Text" size="sm" value={category.promo_text || ""} onChange={handleInputChange} />
      </FormControl>

      <FormControl id="promo_coupon_code">
        <Input name="promo_coupon_code" placeholder="Promo Coupon Code" size="sm" value={category.promo_coupon_code || ""} onChange={handleInputChange} />
      </FormControl>

      <FormControl display="flex" alignItems="center">
        <FormLabel htmlFor="promo_active" mb="0" fontSize="sm">Promo Active</FormLabel>
        <Switch id="promo_active" name="promo_active" isChecked={category.promo_active} onChange={handleInputChange} />
      </FormControl>
    </VStack>
  </fieldset>
</Box>

  {/* ===========================
      SEO SECTION
  =========================== */}
  <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
    <fieldset disabled={!showSaveButton}>
      <Text mb={4} fontSize="md" fontWeight="bold">
        SEO Metadata
      </Text>

      <VStack spacing={4} align="stretch">

        <FormControl id="meta_title">
          <Input
            name="meta_title"
            placeholder="Meta Title"
            size="sm"
            value={category.meta_title || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl id="meta_description">
          <Textarea
            name="meta_description"
            placeholder="Meta Description"
            size="sm"
            value={category.meta_description || ""}
            onChange={handleInputChange}
          />
        </FormControl>

      </VStack>
    </fieldset>
  </Box>

  {/* ===========================
      SYSTEM INFORMATION
  =========================== */}
  <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
    <Text mb={4} fontSize="md" fontWeight="bold">
      System Information
    </Text>

    <VStack spacing={3} align="stretch">

      <Flex justify="space-between">
        <Text fontSize="sm" fontWeight="medium">Sequence</Text>
        <Text fontSize="sm">
          {category.sequence > 0
            ? category.sequence
            : "Not assigned yet"}
        </Text>
      </Flex>

      <Flex justify="space-between">
        <Text fontSize="sm" fontWeight="medium">Created</Text>
        <Text fontSize="sm">
          {category.created_at
            ? new Date(category.created_at).toLocaleString()
            : "-"}
        </Text>
      </Flex>

      <Flex justify="space-between">
        <Text fontSize="sm" fontWeight="medium">Last Updated</Text>
        <Text fontSize="sm">
          {category.updated_at
            ? new Date(category.updated_at).toLocaleString()
            : "-"}
        </Text>
      </Flex>

    </VStack>
  </Box>

      {/* Save Button */}
    {showSaveButton && (
      <Box textAlign="left" mt={4}>
        <Button
          mb={2}
          colorScheme="teal"
          size="sm"
          borderRadius="sm"
          onClick={handleSaveCategory}
          isDisabled={!isCategoryEdited}
        >
          Commit
        </Button>
      </Box>
    )}

</Box>
   
      
    </Box>
  );
};

export default B2CReviewCategory;
